import { evaluateDiscovery } from "./engine";
import { discoveryPricingStatus } from "@/domain/discovery-model";
import {
  validateDiscoveryModel,
  validateJourney,
  type PublishedDiscoveryModel,
} from "@/domain/discovery-model";
import type { Discovery } from "@/domain/types";
import { resumeJourney } from "./journey-state";

export const MODEL_SESSION_PREFIX = "qotta.discovery.v2";
export type ModelSession = {
  version: 2;
  customerId: string;
  discovery: Discovery;
  model: PublishedDiscoveryModel;
  pending: boolean;
  /** Contact draft, separate from the persisted Discovery document. */
  customerNotes?: string;
  /** Retained while the final revision write is pending, even after its active marker closes. */
  revisionId?: string;
};
export function saveModelSession(key: string, value: ModelSession): void {
  if (value.discovery.result && discoveryPricingStatus(value.model.model, value.discovery.answers)) {
    value = { ...value, discovery: { ...value.discovery, result: {
      ...evaluateDiscovery(value.model.model, value.discovery.answers),
      generatedAt: value.discovery.result.generatedAt,
    } } };
  }
  if (typeof window !== "undefined") window.localStorage.setItem(key, JSON.stringify(value));
}
export function loadModelSession(key: string, customerId: string): ModelSession | null {
  if (typeof window === "undefined") return null;
  const raw = window.localStorage.getItem(key);
  if (!raw) return null;
  try {
    const value = JSON.parse(raw) as ModelSession;
    if (
      value.version !== 2 ||
      value.customerId !== customerId ||
      value.discovery.schemaVersion !== 2 ||
      !value.discovery.modelId ||
      value.discovery.modelId !== value.model.id ||
      value.model.runtimeVersion !== 1 ||
      value.model.schemaVersion !== 1 ||
      (value.revisionId !== undefined &&
        (typeof value.revisionId !== "string" || !/^[a-f0-9-]{36}$/.test(value.revisionId)))
    )
      throw new Error("Invalid model binding");
    value.model.model = validateDiscoveryModel(value.model.model);
    value.model.publishedAt = new Date(value.model.publishedAt);
    const d = value.discovery;
    d.startedAt = new Date(d.startedAt);
    d.updatedAt = new Date(d.updatedAt);
    d.expiresAt = new Date(d.expiresAt);
    for (const key of ["firstAnswerAt", "completedAt", "contactRequestedAt"] as const)
      if (d[key]) d[key] = new Date(d[key]);
    if (d.result) d.result.generatedAt = new Date(d.result.generatedAt);
    if (!Number.isFinite(d.startedAt.getTime()) || !Number.isFinite(d.updatedAt.getTime()))
      throw new Error("Invalid dates");
    value.discovery = resumeJourney(value.model.model, d);
    validateJourney(value.model.model, value.discovery);
    return value;
  } catch {
    throw new Error(
      "Saved Discovery or its original model is invalid. Start over explicitly to use the latest model.",
    );
  }
}
